import React, { useState } from 'react';
import axios from 'axios';
import { useAuth } from '../hooks/useAuth';
import { useNavigate } from 'react-router-dom';

export default function AddSong() {
  const { authToken } = useAuth();
  const navigate = useNavigate();
  const [artistQuery, setArtistQuery] = useState('');
  const [artistResults, setArtistResults] = useState([]);
  const [artistId, setArtistId] = useState('');
  const [selectedArtist, setSelectedArtist] = useState(null);
  const [title, setTitle] = useState('');
  const [platformQuery, setPlatformQuery] = useState('');
  const [platforms, setPlatforms] = useState([]);
  const [platform, setPlatform] = useState('');
  const [status, setStatus] = useState('');
  const [loading, setLoading] = useState(false);

  // Autocomplete for artist
  const handleArtistInput = async (e) => {
    const val = e.target.value;
    setArtistQuery(val);
    setArtistId('');
    setSelectedArtist(null);
    // Only clear artistId if user types something different than the selected artist name
    if (val !== '' && val !== artistResults.find(a => a.id === artistId)?.name) {
      setArtistId('');
    }
    if (val && /^[a-zA-Z0-9-]/.test(val)) {
      const letter = val[0].toLowerCase();
      try {
        const res = await axios.get(`/api/artists/${letter}`);
        const matches = Object.entries(res.data)
          .filter(([handle, artist]) =>
            (artist.name && artist.name.toLowerCase().includes(val.toLowerCase())) ||
            (handle && handle.toLowerCase().includes(val.toLowerCase()))
          )
          .map(([handle, artist]) => ({ ...artist, handle }));
        setArtistResults(matches);
      } catch {
        setArtistResults([]);
      }
    } else {
      setArtistResults([]);
    }
  };

  // Autocomplete for platform
  const handlePlatformInput = async (e) => {
    const val = e.target.value;
    setPlatformQuery(val);
    setPlatform(val);
    try {
      const res = await axios.get('/api/platforms');
      setPlatforms(res.data.platforms);
    } catch {
      setPlatforms([]);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    // Validate selectedArtist is set and matches input
    if (!selectedArtist || selectedArtist.handle !== artistQuery) {
      setStatus('Please select a valid artist from the list.');
      return;
    }
    // Validate platform is in platforms
    if (!platforms.includes(platformQuery)) {
      setStatus('Please select a valid platform from the list.');
      return;
    }
    if (!selectedArtist.id || !title || !platformQuery) {
      setStatus('Please fill all fields.');
      return;
    }
    setLoading(true);
    setStatus('');
    try {
      const config = {
        headers: authToken ? { Authorization: `Bearer ${authToken}` } : {},
        withCredentials: true,
      };
      const res = await axios.post('/api/song', {
        artist_id: selectedArtist.id,
        title,
        platform: platformQuery,
      }, config);
      
      setStatus('Song added! Redirecting to upload page...');
      
      // Extract the new song ID from the response
      const songId = res.data.song_id;
      
      // Reset form fields
      setArtistQuery('');
      setArtistId('');
      setSelectedArtist(null);
      setTitle('');
      setPlatform('');
      setPlatformQuery('');
      
      // Navigate to upload page with the new song ID
      setTimeout(() => {
        navigate(`/uploadsong/${songId}`);
      }, 1000); // Short delay to show the success message
    } catch (err) {
      setStatus('Failed to add song.');
    }
    setLoading(false);
  };

  return (
    <div className="max-w-lg mx-auto mt-8 p-4 bg-white rounded shadow">
      <h2 className="text-xl font-bold mb-4">Add New Song</h2>
      <form onSubmit={handleSubmit}>
        <div className="mb-4">
          <label className="block font-semibold mb-1">Artist</label>
          <input
            type="text"
            className="w-full border rounded p-2"
            value={artistQuery}
            onChange={handleArtistInput}
            placeholder="Type artist name..."
            autoComplete="off"
          />
          {artistResults.length > 0 && (
            <ul className="border rounded bg-white mt-1 max-h-32 overflow-y-auto">
              {artistResults.map((artist) => (
                <li
                  key={artist.id}
                  className={`p-2 cursor-pointer hover:bg-blue-100 ${artistId === artist.id ? 'bg-blue-200' : ''}`}
                  onClick={() => {
                    setArtistQuery(artist.handle);
                    setArtistId(artist.id);
                    setSelectedArtist(artist);
                    setArtistResults([]);
                  }}
                >
                  {artist.handle}
                </li>
              ))}
            </ul>
          )}
        </div>
        <div className="mb-4">
          <label className="block font-semibold mb-1">Title</label>
          <input
            type="text"
            className="w-full border rounded p-2"
            value={title}
            onChange={e => setTitle(e.target.value)}
            placeholder="Song title"
            required
          />
        </div>
        <div className="mb-4">
          <label className="block font-semibold mb-1">Platform</label>
          <input
            type="text"
            className="w-full border rounded p-2"
            value={platformQuery}
            onChange={handlePlatformInput}
            placeholder="Type platform..."
            list="platforms-list"
            required
          />
          <datalist id="platforms-list">
            {platforms.map((p) => (
              <option key={p} value={p} />
            ))}
          </datalist>
        </div>
        <button
          type="submit"
          className="bg-blue-600 text-white px-4 py-2 rounded disabled:opacity-50"
          disabled={loading}
        >
          {loading ? 'Adding...' : 'Add Song'}
        </button>
        {status && <div className="mt-2 text-sm">{status}</div>}
      </form>
    </div>
  );
}